import React from "react"
import { Box, Button, Stack, SvgIcon, Typography } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { Link } from "react-router-dom"
import { CarrotClinicProvider } from "../../types/Providers"
import { VirtualServicesTag } from "./VirtualServicesTag"
import { VirtualServiceRestrictionNotice } from "./VirtualServiceRestrictionNotice"
import { UsaStates } from "@internal/constants"
import { Paths } from "#/utils/Paths"

export const CARROT_CLINIC_PROVIDER_UUID = -1

export function CarrotClinicCard({
  provider,
  stateCode
}: {
  provider: CarrotClinicProvider
  stateCode: string | null
}): JSX.Element {
  return (
    <Stack gap={(theme) => theme.tokens.spacing.md}>
      <BrandLabel />
      <Stack gap={(theme) => theme.tokens.spacing.xxs}>
        <Typography variant="h4" component="h2">
          {provider.providerName}
        </Typography>
        <AvailableInState stateCode={stateCode} />
      </Stack>
      <Typography variant="body1">
        <FormattedMessage defaultMessage="Video visits with Carrot clinicians." />
      </Typography>
      <Box>
        <VirtualServicesTag />
      </Box>
      <Box>
        <BookNowButton program={provider.program} />
      </Box>
      {/* Skip when state is known — the BFF already gated on it. */}
      {!stateCode && (
        <VirtualServiceRestrictionNotice virtualServicesNotAvailableIn={provider.virtualServicesNotAvailableIn} />
      )}
    </Stack>
  )
}

function BrandLabel(): JSX.Element {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
    >
      <CarrotBrandMark />
      <Typography variant="chip">
        <FormattedMessage defaultMessage="Carrot's clinical team" />
      </Typography>
    </Box>
  )
}

function CarrotBrandMark(): JSX.Element {
  return (
    <SvgIcon fontSize="small" color="primary" viewBox="0 0 32 32">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.9419 8.81546L9.01584 25.0665H5.33337L14.3344 4.5332H17.6657L26.6667 25.0665H22.8679L15.9419 8.81546Z"
        fill="currentColor"
      />
    </SvgIcon>
  )
}

function AvailableInState({ stateCode }: { stateCode: string | null }): JSX.Element | null {
  const intl = useIntl()
  if (!stateCode) return null
  const state = UsaStates.find((s) => s.id === stateCode)
  if (!state) return null
  const stateName = intl.formatMessage(state.label)
  return (
    <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
      <FormattedMessage defaultMessage="Available across {stateName}" values={{ stateName }} />
    </Typography>
  )
}

function BookNowButton({ program }: { program: CarrotClinicProvider["program"] }): JSX.Element {
  return (
    <Button
      variant="outlined"
      color="secondary"
      component={Link}
      to={{ pathname: Paths.TELEMEDICINE_BOOKING, search: new URLSearchParams({ program }).toString() }}
    >
      <FormattedMessage defaultMessage="Book now" />
    </Button>
  )
}
